import { useEffect, useRef } from "react"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { SEARCH_DEBOUNCE_MS } from "../redux/global-map.constants"
import {
  selectActiveFilters,
  selectActiveLayers,
  selectRecentActivityInViewState,
  selectSearchTerm
} from "../redux/global-map.slice"
import { fetchRecentActivity } from "../redux/global-map.thunks"
import type {
  EntityType,
  GlobalMapFilters,
  RecentActivityByEntityResponse
} from "../redux/global-map.types"

interface UseRecentActivityInViewResult {
  data: RecentActivityByEntityResponse | null
  status: "idle" | "loading" | "refreshing" | "error"
  error: { code: string; retryable: boolean } | null
  refetch: () => void
}

/**
 * Owns what keeps the Map tab's "Recent Activity in View" card in sync —
 * mirrors `useAnalytics.ts` deliberately (mount fetch, filters/search/layers
 * triggers, each guarded by a value-comparison ref rather than a "skip the
 * first run" boolean), against the lighter `/recent-activity` endpoint
 * instead of the full Analytics aggregate. `RecentActivityInView` renders
 * unconditionally on the Map tab (not gated behind a tab switch the way
 * `AnalyticsTab` is), so this hook's own mount effect fires once per Map
 * tab session, same as `useMapFeatures`'s own mount fetch.
 */
export function useRecentActivityInView(): UseRecentActivityInViewResult {
  const dispatch = useAppDispatch()
  const filters = useAppSelector(selectActiveFilters)
  const searchTerm = useAppSelector(selectSearchTerm)
  const activeLayers = useAppSelector(selectActiveLayers)
  const { data, status, error } = useAppSelector(
    selectRecentActivityInViewState
  )

  const hasFetchedInitial = useRef(false)
  const lastFetchedFiltersRef = useRef<GlobalMapFilters | null>(null)
  const lastFetchedLayersRef = useRef<EntityType[] | null>(null)
  const lastFetchedSearchRef = useRef(searchTerm)

  useEffect(() => {
    hasFetchedInitial.current = true
    lastFetchedFiltersRef.current = filters
    lastFetchedLayersRef.current = activeLayers

    dispatch(
      fetchRecentActivity({
        filters,
        search: searchTerm,
        entities: activeLayers
      })
    )
    // Mount-only — see `useAnalytics.ts`'s identical mount effect for why
    // `[dispatch]` alone is the deliberate trigger.
  }, [dispatch])

  useEffect(() => {
    if (!hasFetchedInitial.current) return
    if (filters === lastFetchedFiltersRef.current) return
    lastFetchedFiltersRef.current = filters

    dispatch(
      fetchRecentActivity({
        filters,
        search: searchTerm,
        entities: activeLayers
      })
    )
  }, [filters, dispatch])

  useEffect(() => {
    if (searchTerm === lastFetchedSearchRef.current) return
    lastFetchedSearchRef.current = searchTerm

    const handle = setTimeout(() => {
      dispatch(
        fetchRecentActivity({
          filters,
          search: searchTerm,
          entities: activeLayers
        })
      )
    }, SEARCH_DEBOUNCE_MS)

    return () => clearTimeout(handle)
  }, [searchTerm, dispatch])

  useEffect(() => {
    if (!hasFetchedInitial.current) return
    if (activeLayers === lastFetchedLayersRef.current) return
    lastFetchedLayersRef.current = activeLayers

    dispatch(
      fetchRecentActivity({
        filters,
        search: searchTerm,
        entities: activeLayers
      })
    )
  }, [activeLayers, dispatch])

  const refetch = () => {
    dispatch(
      fetchRecentActivity({
        filters,
        search: searchTerm,
        entities: activeLayers
      })
    )
  }

  return { data, status, error, refetch }
}
